import { useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { Modal } from "../ui/Modal";
import type {
  AdminCourseExamItem,
  AdminCourseModuleItem,
  AdminCourseNoteItem,
  AdminCourseQuizItem,
} from "../../lib/api";

type PreviewItem =
  | { kind: "module"; item: AdminCourseModuleItem }
  | { kind: "exam"; item: AdminCourseExamItem }
  | { kind: "quiz"; item: AdminCourseQuizItem }
  | { kind: "note"; item: AdminCourseNoteItem };

export function CourseStudentPreviewModal({
  preview,
  onClose,
}: {
  preview: PreviewItem;
  onClose: () => void;
}) {
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const { item, kind } = preview;
  const questions = kind === "quiz" && Array.isArray(item.questions) ? item.questions : [];

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="xl"
      title={
        <span className="block min-w-0">
          <span className="block text-xs font-semibold uppercase tracking-wide text-accent-foreground">Student view</span>
          <span className="block truncate">{item.title}</span>
        </span>
      }
      description="Read-only simulation. Your course page and tab state remain unchanged."
    >
      <div className="space-y-4">
          {kind === "module" && (
            <section className="rounded-lg border border-border bg-card p-5 space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Course module</p>
              <h3 className="text-xl font-bold">{item.title}</h3>
              <p className="text-sm text-muted-foreground">
                This is how the module appears in the student library. Student access is{" "}
                {item.isFree === false ? "premium" : "free"} and the current status is{" "}
                {item.status.toLowerCase()}.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
                <Info label="Pages" value={String(item.pageCount || 0)} />
                <Info label="Size" value={`${Math.round((item.fileSize || 0) / 1024)} KB`} />
                <Info label="Visibility" value={item.isHidden ? "Hidden" : "Visible"} />
              </div>
            </section>
          )}

          {kind === "exam" && (
            <section className="rounded-lg border border-border bg-card p-5 space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Past exam</p>
              <h3 className="text-xl font-bold">{item.title}</h3>
              <p className="text-sm text-muted-foreground">
                Students see this exam as a practice activity after it is published.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
                <Info label="Year" value={item.examYear || "—"} />
                <Info label="Questions" value={String(item.questionCount || 0)} />
                <Info label="Access" value={item.isFree === false ? "Premium" : "Free"} />
              </div>
            </section>
          )}

          {kind === "note" && (
            <article className="rounded-lg border border-border bg-card p-5">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">
                {item.moduleTitle} · {item.chapterTitle}
              </p>
              <h3 className="text-xl font-bold mb-4">{item.title}</h3>
              <div className="whitespace-pre-wrap leading-7 text-sm text-foreground">
                {item.markdown || "No student-facing note content is available."}
              </div>
            </article>
          )}

          {kind === "quiz" && (
            <section className="space-y-4">
              {questions.length === 0 ? (
                <p className="rounded-lg border border-border bg-card p-5 text-sm text-muted-foreground">
                  No quiz questions are available for this preview.
                </p>
              ) : questions.map((question: any, index) => {
                const options = Array.isArray(question.options) ? question.options : [];
                const selected = answers[index];
                return (
                  <article key={index} className="rounded-lg border border-border bg-card p-5 space-y-3">
                    <p className="font-semibold">
                      {index + 1}. {question.question || question.text || question.stem}
                    </p>
                    <div className="space-y-2">
                      {options.map((option: string, optionIndex: number) => (
                        <button
                          key={optionIndex}
                          type="button"
                          onClick={() => setAnswers((previous) => ({ ...previous, [index]: optionIndex }))}
                          className={`w-full text-left rounded-md border px-3 py-2 text-sm transition ${
                            selected === optionIndex ? "border-primary bg-primary/10" : "border-border hover:bg-muted"
                          }`}
                        >
                          {String.fromCharCode(65 + optionIndex)}. {option}
                        </button>
                      ))}
                    </div>
                    {selected !== undefined && (
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <CheckCircle2 size={13} className="text-primary" /> Answer selected for preview only.
                      </p>
                    )}
                  </article>
                );
              })}
            </section>
          )}
      </div>
    </Modal>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md bg-muted/60 border border-border p-3">
      <p className="text-muted-foreground">{label}</p>
      <p className="font-semibold text-foreground mt-1">{value}</p>
    </div>
  );
}
